import { createApi } from "@reduxjs/toolkit/query/react";
import { createAuthBaseQuery } from "../auth/reauthBaseQuery";
import { appConfig } from "../../appConfig";
import { EntityCommonInfoModel, ApiMessageResponse, PagedResponse } from "../../entities/common";
import {
  RoleModel,
  UserModel,
  UserEditorModel,
  UserMultiDeleteModel,
  UserPolicyModel,
  UserSetVipModel,
  UserSubscriptionModel,
  UserListRequest,
} from "../../entities/user";
import { LoginModel } from "../../entities/auth";
import { getOrCreateDeviceId } from "../../utils/deviceId";
import { defaultPagination, getPaginationFromMeta } from "../pagination";

export enum TagType {
  User = "User",
  Policies = "Policies",
  UserPolicies = "UserPolicies",
  Role = "Role",
  ScoringConfig = "ScoringConfig",
  ScoringLog = "ScoringLog",
  Message = "Message",
  Notification = "Notification",
}

export const identitySlice = createApi({
  reducerPath: "identity",
  baseQuery: createAuthBaseQuery(`${appConfig.identityUrl}/api`),
  tagTypes: Object.values(TagType),
  keepUnusedDataFor: 3600,

  endpoints: (builder) => ({
    getCurrentUser: builder.query<UserModel, void>({
      query: () => "/identity/current-user",
      providesTags: (result) =>
        result
          ? [
              { type: TagType.User, id: result.id },
              { type: TagType.User, id: "CURRENT_USER" },
            ]
          : [{ type: TagType.User, id: "CURRENT_USER" }],
    }),

    login: builder.mutation<ApiMessageResponse, LoginModel>({
      query: (body) => ({
        url: "/identity/login",
        method: "POST",
        body: { ...body, deviceId: getOrCreateDeviceId() },
      }),
      invalidatesTags: [{ type: TagType.User, id: "CURRENT_USER" }],
    }),

    logout: builder.mutation<ApiMessageResponse, void>({
      query: () => ({ url: "/identity/logout", method: "POST" }),
      invalidatesTags: [{ type: TagType.User, id: "CURRENT_USER" }],
    }),

    // User
    getUsers: builder.query<UserModel[], void>({
      query: () => "/users/all",
      providesTags: (result = [], error, arg) => [{ type: TagType.User, id: "LIST" }],
    }),
    getUserCommonInfo: builder.query<EntityCommonInfoModel, void>({
      query: () => "/users/commonInfo",
      providesTags: [{ type: TagType.User, id: "LIST" }],
    }),
    getUsersPaged: builder.query<PagedResponse<UserSubscriptionModel>, UserListRequest>({
      query: (request) => ({
        url: "/users/paginated",
        params: request,
      }),
      transformResponse: (response: UserSubscriptionModel[], meta): PagedResponse<UserSubscriptionModel> => ({
        items: response,
        pagination: getPaginationFromMeta(meta) ?? defaultPagination(response.length),
      }),
      providesTags: (result) =>
        result
          ? [
              { type: TagType.User, id: "PARTIAL-LIST" },
              ...result.items.map(({ id }) => ({
                type: TagType.User,
                id,
              })),
            ]
          : [{ type: TagType.User, id: "PARTIAL-LIST" }],
    }),
    getUsersSelectFilter: builder.query<UserModel[], string>({
      query: (filterText: string) => `/users/select/${filterText}`,
      providesTags: (data = [], error, arg) => [
        { type: TagType.User, id: "PARTIAL-LIST" },
        ...data.map(({ id }: { id: number | string }) => ({
          type: TagType.User,
          id,
        })),
      ],
    }),
    getUser: builder.query<UserModel, string>({
      query: (userId) => `/users/${userId}`,
      providesTags: (result, error, arg) => [{ type: TagType.User, id: arg }],
    }),
    addUser: builder.mutation({
      query: (initialUser: UserEditorModel) => ({
        url: "/users",
        method: "POST",
        body: initialUser,
      }),
      invalidatesTags: [
        { type: TagType.User, id: "LIST" },
        { type: TagType.User, id: "PARTIAL-LIST" },
      ],
    }),
    editUser: builder.mutation({
      query: (userModel: UserEditorModel) => ({
        url: `/users/${userModel.id}`,
        method: "PUT",
        body: userModel,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagType.User, id: arg.id }],
    }),
    confirmUserPhoneNumber: builder.mutation({
      query: (userId: string) => ({
        url: `/users/confirmPhoneNumber/${userId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagType.User, id: arg }],
    }),
    setVipUser: builder.mutation<UserModel, UserSetVipModel>({
      query: (model) => ({
        url: "/users/vip-set",
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagType.User, id: arg.userId }],
    }),
    deleteUser: builder.mutation({
      query: (userId: string) => ({
        url: `/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagType.User, id: arg },
              { type: TagType.User, id: "LIST" },
            ],
    }),
    deleteMultiUsers: builder.mutation({
      query: (multiDeleteModel: UserMultiDeleteModel) => ({
        url: "/users/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: string) => ({
                type: TagType.User,
                id,
              })),
              { type: TagType.User, id: "LIST" },
            ],
    }),

    // Policies

    getPolicies: builder.query<string[], undefined>({
      query: () => "/policies",
      providesTags: (result, error, arg) => [{ type: TagType.Policies, id: "LIST" }],
    }),

    getUserPolicies: builder.query<UserPolicyModel, string>({
      query: (userId) => `/policies/userPolicies/${userId}`,
      providesTags: (result, error, arg) => [{ type: TagType.UserPolicies, id: arg }],
    }),

    addRemoveUserPolicies: builder.mutation({
      query: (model: UserPolicyModel) => ({
        url: "/policies/userPolicies",
        method: "POST",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagType.UserPolicies, id: arg.userId }],
    }),

    // rols

    getRoles: builder.query<RoleModel[], undefined>({
      query: () => "/roles",
      providesTags: (data = [], error, arg) => [
        { type: TagType.Role, id: "LIST" },
        ...data.map(({ id }: { id: number | string }) => ({
          type: TagType.Role,
          id,
        })),
      ],
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useLoginMutation,
  useLogoutMutation,

  useGetUsersQuery,
  useGetUserCommonInfoQuery,
  useGetUsersPagedQuery,
  useGetUsersSelectFilterQuery,
  useGetUserQuery,
  useAddUserMutation,
  useEditUserMutation,
  useConfirmUserPhoneNumberMutation,
  useSetVipUserMutation,
  useDeleteUserMutation,
  useDeleteMultiUsersMutation,

  useGetPoliciesQuery,
  useGetUserPoliciesQuery,
  useAddRemoveUserPoliciesMutation,

  useGetRolesQuery,
} = identitySlice;
